import { isEmpty } from "lodash";
import React, { Fragment, useEffect, useState } from "react";
import { Button, Card, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link } from 'react-router-dom';
import { Nullable } from "../../../../types/utils";
import * as Api from "../../../api";
import { OrderCategory, TApiOrderDetails } from "../../../api/endpoints/Order";
import { TOrderProduct } from "../../../api/endpoints/OrderProduct";
import Col from "../../../components/Common/Col";
import Row from "../../../components/Common/Row";
import SelectedParametersList from "../../../components/EuCutOutConfigurator/ParametersSelector/SelectedParametersList";
import LoadingIndicator from "../../../components/Feedback/LoadingIndicator";
import {
  AdditionalChargesFootnote,
  LimitCapacityFootnoteLegacy,
  ResearchAndSmallBusinessFootnote
} from "../../../components/Footnotes";
import { MailLink } from "../../../components/Navigation/Links/ExternalLink";
import OrderName from "../../../components/OrderName";
import OrderPDFButton from "../../../components/OrderPDFButton";
import OrderSubmissionForm from "../../../components/OrderSubmissionForm";
import OrderSubmissionFormCompatibility from "../../../components/OrderSubmissionFormCompatibility";
import Disclaimer from "../../../components/Ui/Disclaimer";
import ReferenceNumber from "../../../components/Ui/ReferenceNumber";
import { UserType } from "../../../entities/User";
import useOrderCostingVersion from "../../../hooks/useOrderCostingVersion";
import DisseminationRequestPolicy from "../../../policies/DisseminationRequestPolicy";
import OrderPolicy from "../../../policies/OrderPolicy";
import { paths } from "../../../router";
import { selectParameters } from "../../../store/selectors/new/parameters";
import DateTime from "../../../utils/DateTime";
import EuCutOutProductHelpers from "../../../utils/EuCutOutProductHelpers";
import { formatVolumeBand } from "../../../utils/Format";
import OrderHelpers from "../../../utils/OrderHelpers";
import OrderExpirationAlert from "../ExpirationAlert";
import LicenceDetails from "../LicenceDetails";
import OrderCostSummary from "../OrderCostSummary";
import OrderHeader from "../OrderHeader";
import { OrderProps } from "../types";
import EuCutOutProductCost from "./EuCutOutProductCost";

type Props = OrderProps & {
  order: TApiOrderDetails<OrderCategory.EU_CUTOUT>;
}


const EuCutOutOrder = (props: Props) => {
  const {
    user,
    order,
    sets,
    chosenProvider,
    chosenDataDeliverer,
    chosenServicePackage,
    chosenVolumeBand,
    onSubmitClick,
    submissionDialogOpen,
    setSubmissionDialogOpen,
    onSubmissionComplete,
    onApprove,
    onReject,
    onCancel
  } = props;

  const availableParams = useSelector(selectParameters);
  const [product, setProduct] = useState<Nullable<TOrderProduct<OrderCategory.EU_CUTOUT>>>(null);
  const [error, setError] = useState<Nullable<string>>(null);
  const hasCostError = Boolean(order.cost.data && order.cost.data.error);
  const { orderCostingVersions, currentOrderCostingVersion } = useOrderCostingVersion(order);
  const isOrderExpired = OrderHelpers.isExpired(order);

  useEffect(() => {
    Api.OrderProduct.list(order.id)
      .then((products) => setProduct(products[0]))
      .catch(() => setError('Sorry! We were unable to fetch products.'));
  }, []);


  if (!product || !orderCostingVersions || isEmpty(availableParams)) {
    return <LoadingIndicator />;
  }

  const selection = EuCutOutProductHelpers.deriveParameterSelection(product, sets, availableParams);

  return (
    <Fragment>
      {
        isOrderExpired &&
        <OrderExpirationAlert />
      }

      <OrderHeader order={order} />

      <Row className="margin-bottom-xs">
        <Col>
          <strong>Order name</strong>: <OrderName order={order} />
        </Col>
      </Row>

      {
        user.type !== UserType.Customer &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Customer:</strong> {order.customer}
            &nbsp;(<MailLink email={order.email} />)
          </Col>
        </Row>
      }

      {
        order.start_date &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Start date:</strong> {DateTime.Format.dateLong(order.start_date)}
          </Col>
        </Row>
      }

      {
        chosenProvider &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Licensor:</strong> {chosenProvider.name}
          </Col>
        </Row>
      }

      {
        chosenDataDeliverer &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Data deliverer:</strong> {chosenDataDeliverer.name}
          </Col>
        </Row>
      }

      {
        chosenServicePackage &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Service Package:</strong> {chosenServicePackage.title}
          </Col>
        </Row>
      }

      {
        chosenVolumeBand &&
        <Row className="margin-bottom-xs">
          <Col>
            <strong>Volume Band:</strong> {formatVolumeBand(chosenVolumeBand)} per day
          </Col>
        </Row>
      }

      <LicenceDetails order={order} user={user} />

      <Row className="margin-bottom-xs">
        <Col>
          <ReferenceNumber referenceNumber={order.id} userType={user.type} />
        </Col>
      </Row>

      <Card className="margin-bottom-sm">
        {
          product &&
          <Card.Body>
            <div className="margin-bottom-md">
              <div className="margin-bottom-xs">
                <strong>Parameters:</strong>
              </div>
              <SelectedParametersList selection={selection} />
            </div>

            <div className="margin-bottom-md">
              <strong>High frequency products:</strong>
              &nbsp;{product.data.high_frequency ? 'Yes' : 'No'}
            </div>

            {
              order.cost.version === 'v1' &&
              <EuCutOutProductCost order={order} />
            }

            {
              !isEmpty(order.cost) &&
              <OrderCostSummary
                order={order}
                annualTotalCostFootnoteRef="*"
              />
            }
          </Card.Body>
        }
      </Card>

      <Row>
        <Col>
          <div style={{ display: 'flex' }}>
            {
              OrderPolicy.canApprove(user, order) &&
              <Fragment>
                <Button
                  onClick={onApprove}
                  style={{ marginRight: '2em' }}
                  variant="success"
                >
                  Approve order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canReject(user, order) &&
              <Fragment>
                <Button
                  onClick={onReject}
                  style={{ marginRight: '2em' }}
                  variant="danger"
                >
                  Reject order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canCancel(user, order) &&
              <Fragment>
                <Button
                  onClick={onCancel}
                  style={{ marginRight: '2em' }}
                  variant="danger"
                >
                  Cancel order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canSubmit(user, order, orderCostingVersions) &&
              <Fragment>
                <Button
                  onClick={onSubmitClick}
                  style={{ marginRight: '2em' }}
                  disabled={hasCostError}
                >
                  Submit order
                </Button>
              </Fragment>
            }

            {
              OrderPolicy.canEdit(user, order) &&
              <Fragment>
                <Button
                  as={Link}
                  to={paths.euCutOutOrderEdit(order.id)}
                  style={{ marginRight: '2em' }}
                >
                  Edit order
                </Button>
              </Fragment>
            }


            <Modal size="xl" show={submissionDialogOpen} onHide={() => setSubmissionDialogOpen(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Submit order</Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <OrderSubmissionFormCompatibility order={order}>
                  {
                    currentOrderCostingVersion &&
                    <OrderSubmissionForm
                      order={order}
                      orderCostingVersion={currentOrderCostingVersion}
                      onComplete={onSubmissionComplete}
                    />
                  }
                </OrderSubmissionFormCompatibility>
              </Modal.Body>
            </Modal>

            <OrderPDFButton
              order={order}
              currentCostingVersion={currentOrderCostingVersion}
              orderProducts={product ? [product] : []}
              sets={sets}
              provider={chosenProvider}
              dataDeliverer={chosenDataDeliverer}
              servicePackage={chosenServicePackage}
              volumeBand={chosenVolumeBand}
              disabled={!order || !product || hasCostError}
            />

            {
              DisseminationRequestPolicy.canAccess(user, order) && !hasCostError &&
              <Button
                as={Link}
                to={paths.orderDisseminationRequest(order.id)}
                style={{ marginLeft: '2em' }}
                variant="primary"
                className="btn__inverted"
              >
                Dissemination Request
              </Button>
            }
          </div>
        </Col>
      </Row>

      <div className="margin-bottom-xs margin-top-md">
        <Disclaimer identifier="*">
          <p>
            <AdditionalChargesFootnote />
          </p>

          <p>
            <ResearchAndSmallBusinessFootnote />
          </p>

          <p>
            <LimitCapacityFootnoteLegacy />
          </p>
        </Disclaimer>
      </div>
    </Fragment>
  )
}

export default EuCutOutOrder;
